import React from 'react';
import {
  Stack,
  Text,
  DefaultButton,
  Image,
  IStackTokens,
  IComboBoxOption,
  Label,
  Icon,
} from '@fluentui/react';
import { AppContext } from '../../AppInRouterContext';
import useAsyncDataCourier from '../../common/custom-hooks/useAsyncDataCourier';
import ComboBoxSearch from '../Common/ComboBoxSearch';
import { Club } from '../../system/Club/Club';
import { TextField } from '@fluentui/react';
import { Jugador } from '../../system/Jugador/Jugador';
import DialogTemplate from '../Common/DialogTemplate';
import { DatosIniciales } from './Steps/DatosIniciales';
import { SubirComprobanteMedico } from './Steps/SubirComprobanteMedico';
import { Contacto } from './Steps/Contacto';
import { AptoFisico } from '../../system/AptoFisico/AptoFisico';
import { Confirmacion } from './Steps/Confirmacion';

interface IWizardAptoFisico {
  appContext: AppContext;
  getToken: () => Promise<string>;
}

export const checkMaxSize = (sizeInBytes: number) => {
  const limit = 20 * 1024 * 1024; // 50MB
  return sizeInBytes < limit;
};

const outerStackTokens: IStackTokens = { childrenGap: 20 };
const bodyStackTokens: IStackTokens = { childrenGap: 10 };

export const WizardAptoFisico = (props: IWizardAptoFisico) => {
  const [step, setStep] = React.useState(0);
  const [jugador, setJugador] = React.useState<Jugador | undefined>(undefined);
  const [showNotFound, setShowNotFound] = React.useState<boolean>(false);
  const [showReplaceComprobante, setShowReplaceComprobante] = React.useState<boolean>(false);

  // Paso buscar jugador
  const [selectedClubId, setSelectedClubId] = React.useState<string>('');
  const [selectedTipoDocumentoId, setSelectedTipoDocumentoId] = React.useState<string>('01');
  const [documentoNumero, setDocumentoNumero] = React.useState<string>('');

  // Paso comprobante
  const [nombre, setNombre] = React.useState<string>('');
  const [apellido, setApellido] = React.useState<string>('');
  const [fileComprobanteMedico, setFileComprobanteMedico] = React.useState<File | null>(null);

  // Paso contacto
  const [email, setEmail] = React.useState<string>('');
  const [telefono, setTelefono] = React.useState<string>('');
  const [fileSelfie, setFileSelfie] = React.useState<File | null>(null);

  const clubes = useAsyncDataCourier(
    () => props.appContext.system.clubes(),
    [],
    props.appContext.courier,
    props.appContext.changeBlockUI
  );

  const documentoTipos = useAsyncDataCourier(
    () => props.appContext.system.documentoTipos(),
    [],
    props.appContext.courier,
    props.appContext.changeBlockUI
  );

  const apertura = useAsyncDataCourier(
    () => props.appContext.system.aperturaVigente(),
    [],
    props.appContext.courier,
    props.appContext.changeBlockUI
  );

  const reset = () => {
    setStep(0);
    setJugador(undefined);
    setShowNotFound(false);
    setShowReplaceComprobante(false);
    setSelectedClubId('');
    setSelectedTipoDocumentoId('01');
    setDocumentoNumero('');
    setNombre('');
    setApellido('');
    setFileComprobanteMedico(null);
    setEmail('');
    setTelefono('');
    setFileSelfie(null);
  };

  const populateData = (jugador: Jugador) => {
    setNombre(jugador.getNombre());
    setApellido(jugador.getApellido());
    setEmail(jugador.getEmail());
    setTelefono(jugador.getNroTelefono());
  };

  const estaVigente = async (jugador: Jugador) => {
    let ret: any = undefined;
    try {
      props.appContext.changeBlockUI(true);
      ret = await props.appContext.system.puedeCargarComprobante(jugador);
    } catch (err) {
      props.appContext.courier.messageError(err);
    } finally {
      props.appContext.changeBlockUI(false);
    }
    return ret;
  };

  const buscarJugador = async () => {
    if (selectedClubId && selectedTipoDocumentoId && documentoNumero) {
      try {
        props.appContext.changeBlockUI(true);
        const token = await props.getToken();
        const _jugador = await props.appContext.system.buscarJugador(
          selectedClubId,
          documentoNumero.replaceAll('.', ''),
          selectedTipoDocumentoId!,
          token
        );

        if (_jugador) {
          let vigente = await estaVigente(_jugador);
          if (vigente && vigente.status) {
            setJugador(_jugador);
            populateData(_jugador);
            const nextStep = step + 1;
            setStep(nextStep);
          } else if (vigente && !vigente.status) {
            if (vigente.showReplaceComprobante) {
              setShowReplaceComprobante(true);
              setJugador(_jugador);
              populateData(_jugador);
            }

            props.appContext.courier.messageError(vigente.msg);
          }
        } else {
          setShowNotFound(true);
        }
      } catch (err) {
        props.appContext.courier.messageError(err);
      } finally {
        props.appContext.changeBlockUI(false);
      }
    }
  };

  const save = async () => {
    if (fileComprobanteMedico && jugador && apertura) {
      try {
        props.appContext.changeBlockUI(true);
        const aptoFisico = AptoFisico.build(
          jugador,
          nombre,
          apellido,
          email,
          telefono,
          apertura,
          fileComprobanteMedico,
          fileSelfie
        );
        const token = await props.getToken();
        const ret = await props.appContext.system.save(aptoFisico, fileComprobanteMedico, fileSelfie, token);
        const nextStep = step + 1;
        setStep(nextStep);
      } catch (err) {
        props.appContext.courier.messageError(err);
      } finally {
        props.appContext.changeBlockUI(false);
      }
    }
  };

  const back = () => step > 0 && setStep(step - 1);
  const next = async () => {
    if (step === 0) {
      await buscarJugador();
    }
    if (step === 1) {
      if (fileComprobanteMedico && nombre && apellido && checkMaxSize(fileComprobanteMedico.size)) {
        const nextStep = step + 1;
        setStep(nextStep);
      }
    }

    if (step === 2) {
      await save();
    }
  };

  const disableNext = () => {
    switch (step) {
      case 0:
        return !selectedClubId || !selectedTipoDocumentoId || !documentoNumero || !apertura; // Club, tipo de documento y número de documento son obligatorios para el paso 0
      case 1:
        return !fileComprobanteMedico || !nombre || !apellido || !checkMaxSize(fileComprobanteMedico.size);
      case 2:
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !regex.test(email);
    }
  };

  const getSubtitleByStep = () => {
    switch (step) {
      case 0:
        return 'Ingresá tus datos';
      case 1:
        return 'Subí el comprobante médico';
      case 2:
        return 'Completá tus datos de contacto';
      case 3:
        return 'Confirmación';
    }
  };

  return (
    <Stack
      verticalAlign="center"
      horizontalAlign="center"
      styles={{
        root: {
          height: '100vh',
          width: '100vw',
          backgroundColor: 'white', // Color de fondo para todo el contenedor
          color: 'white !important',
        },
      }}
    >
      <Stack
        tokens={outerStackTokens}
        styles={{
          root: {
            width: 370,
            // height: 550,
            // boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            // borderRadius: 8,
            padding: 20,
            // backgroundColor: '#ffffff',
          },
        }}
      >
        {/* Logo */}
        <Image height={100} src="/ahba-logo.png" alt="Logo" styles={{ root: { alignSelf: 'center' } }} />

        {/* Título */}
        <Text variant="xLarge" styles={{ root: { textAlign: 'center', fontWeight: 'bold' } }}>
          AAHBA / APTO MEDICO
          <Text block variant="small" styles={{ root: { textAlign: 'center', color: '#666' } }}>
            {apertura ? `Temporada: ${apertura.getTemporada()?.getDescripcion()}` : 'No hay apertura vigente'}
          </Text>
        </Text>

        {/* Subtítulo */}
        <Text variant="medium" styles={{ root: { textAlign: 'center', color: '#666' } }}>
          {getSubtitleByStep()}
        </Text>

        {/* Cuerpo */}
        <Stack
          grow
          tokens={bodyStackTokens}
          styles={{
            root: {
              flexGrow: 1,
              // minHeight: 300,
              // maxHeight: 300,
              // overflowY: 'auto', // Por si el contenido es largo
              // border: '1px solid #ddd',
              // borderRadius: 4,
              // padding: 10,
            },
          }}
        >
          {step === 0 && clubes && documentoTipos && (
            <DatosIniciales
              selectedClubId={selectedClubId}
              setSelectedClubId={setSelectedClubId}
              documentoNumero={documentoNumero}
              setDocumentoNumero={setDocumentoNumero}
              selectedTipoDocumentoId={selectedTipoDocumentoId}
              setSelectedTipoDocumentoId={setSelectedTipoDocumentoId}
              documentoTipos={documentoTipos}
              clubes={clubes}
              isDni={selectedTipoDocumentoId === '01'}
            ></DatosIniciales>
          )}
          {step === 1 && jugador && (
            <SubirComprobanteMedico
              jugador={jugador}
              nombre={nombre}
              apellido={apellido}
              setNombre={setNombre}
              setApellido={setApellido}
              onDropComprobanteMedico={setFileComprobanteMedico}
              back={() => setStep(step - 1)}
              tipoDocumento={
                documentoTipos?.find((dt) => dt.getId() === selectedTipoDocumentoId)?.getDescripcion() || ''
              }
              numeroDocumento={documentoNumero}
              fileComprobanteMedico={fileComprobanteMedico}
              appContext={props.appContext}
              isDni={selectedTipoDocumentoId === '01'}
            ></SubirComprobanteMedico>
          )}
          {step === 2 && jugador && (
            <Contacto
              jugador={jugador}
              telefono={telefono}
              email={email}
              setTelefono={setTelefono}
              setEmail={setEmail}
              onDropSelfie={setFileSelfie}
              fileSelfie={fileSelfie}
            ></Contacto>
          )}
          {step === 3 && jugador && (
            <Confirmacion jugador={jugador} showEmailLegend={!!email} reset={reset}></Confirmacion>
          )}
        </Stack>

        {/* Botón de Siguiente */}
        {step !== 3 && (
          <DefaultButton
            disabled={disableNext()}
            onClick={next}
            text="Siguiente"
            styles={{ root: { width: '100%' } }}
            primary
          />
        )}

        {step !== 0 && step !== 3 && (
          <Stack className="">
            <Text variant="medium" className="mt-1 link-styles">
              <span onClick={back}>Volver</span>
            </Text>
          </Stack>
        )}
        <Stack>
          <Text variant="tiny" styles={{ root: { textAlign: 'center', color: '#666' } }}>
            LarrySport - Todos los derechos reservados
          </Text>
        </Stack>
      </Stack>

      <DialogTemplate
        showDialog={showNotFound}
        toggleShowDialog={() => {
          setShowNotFound(!showNotFound);
        }}
        title={'No te encontramos'}
        onAccept={() => setShowNotFound(!showNotFound)}
        onCancel={() => {}}
        acceptText={'Entendido'}
        cancelText={''}
        hideCancelButton
        maxWidth={250}
      >
        <div>
          <Text block>
            Si tu club ({clubes?.find((c) => c.getId() === selectedClubId)?.getNombre()}) y tu documento (
            {documentoNumero}) son correctos
          </Text>
          <Text block className="mt-1">
            <Icon
              iconName="AlertSolid"
              className="error-color"
              style={{ position: 'relative', top: 2, marginRight: 3 }}
            ></Icon>
            Entonces, comunicate con tu delegado para tramitar el registro de tus datos
          </Text>
          <Text block className="mt-2">
            Si no, verificá si tus datos están bien ingresados.
          </Text>
        </div>
      </DialogTemplate>

      <DialogTemplate
        showDialog={showReplaceComprobante}
        toggleShowDialog={() => {
          setShowReplaceComprobante(!showReplaceComprobante);
        }}
        title={'Ya tenes un comprobante de medico'}
        onAccept={() => {
          setShowReplaceComprobante(!showReplaceComprobante);
          setStep(step + 1);
        }}
        onCancel={() => {
          setShowReplaceComprobante(!showReplaceComprobante);
        }}
        acceptText={'Si'}
        cancelText={'No'}
        maxWidth={200}
      >
        <Text>Queres cargar uno nuevo y reemplazarlo?</Text>
      </DialogTemplate>
    </Stack>
  );
};
